export const GUEST_CART_PROXY_BASE_URL = '/api/commerce-guest-cart';

type GuestCartProxyRoute = { method: string; template: string };

const CART_ROUTE = 'commerce/bridge/v1/cart';

/**
 * Bridge routes commerce-core calls for a signed-out visitor's cart; the
 * server route and the client both read this. User-scoped routes are not here.
 */
export const GUEST_CART_PROXY_ROUTES: readonly GuestCartProxyRoute[] = [
  { method: 'POST', template: `${CART_ROUTE}/create` },
  { method: 'GET', template: `${CART_ROUTE}/:id` },
  { method: 'POST', template: `${CART_ROUTE}/:id/line-item/add` },
  { method: 'POST', template: `${CART_ROUTE}/:id/line-item/remove` },
  { method: 'PUT', template: `${CART_ROUTE}/:id/line-item/:id` },
  { method: 'POST', template: `${CART_ROUTE}/:id/replace-line-items` },
  { method: 'PUT', template: `${CART_ROUTE}/:id/change-currency` },
];

const PARAM = ':id';
const ID_SEGMENT = /^[A-Za-z0-9-]{1,64}$/;

export const isGuestCartId = (value: unknown): value is string =>
  typeof value === 'string' && ID_SEGMENT.test(value);

const segmentsOf = (path: string | undefined) =>
  (path ?? '').replace(/^\/+|\/+$/g, '').split('/');

const upstreamPathFor = (
  route: GuestCartProxyRoute,
  segments: string[]
): string | undefined => {
  const template = route.template.split('/');
  if (template.length !== segments.length) return undefined;
  const matches = template.every((part, index) =>
    part === PARAM ? isGuestCartId(segments[index]) : part === segments[index]
  );
  return matches ? segments.join('/') : undefined;
};

/** The validated path to call upstream, or undefined when the route is not allowed. */
export const guestCartUpstreamPath = (
  method: string | undefined,
  path: string | undefined
): string | undefined => {
  const normalizedMethod = method?.toUpperCase();
  const segments = segmentsOf(path);
  for (const route of GUEST_CART_PROXY_ROUTES) {
    if (route.method !== normalizedMethod) continue;
    const upstreamPath = upstreamPathFor(route, segments);
    if (upstreamPath) return upstreamPath;
  }
  return undefined;
};

/** The cart id segment of a cart route, or `create` for the create route. */
export const guestCartIdIn = (path: string | undefined): string | undefined => {
  const segments = segmentsOf(path);
  return segments[3] === 'cart' ? segments[4] : undefined;
};

export const isGuestCartProxyRoute = (
  method: string | undefined,
  path: string | undefined
): boolean => guestCartUpstreamPath(method, path) !== undefined;
